import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MousePointer2 } from "lucide-react";
import { getCifras } from "@/lib/api";
import Pruebala, { useMuestra } from "@/components/portada/Pruebala";
import { PILLARS } from "@/data/modules";
import { adelantaSinPrisa, pistasDe } from "@/lib/adelanta.mjs";

// El destino de las cinco píldoras y del botón: una sola expresión, porque webpack decide
// qué trozo es cuál por la expresión literal del `import()` y no por el módulo.
const TRAE_CURSO = () => import("@/pages/CursoDetail");
const TRAE_CURSOS = () => import("@/pages/Cursos");

// LAS ESTRELLAS DEL TITULAR, en porcentajes y con su retraso para que no parpadeen a la vez
// —un cielo que late a compás es una guirnalda, no un cielo—. Van todas por FUERA del pasillo
// central (22 % – 78 %), que es por donde cae el texto: una estrella detrás de una letra no es
// un adorno, es ruido encima del contraste. Son las mismas que se encienden en la tarjeta del
// mundo, en `components/mundo/PuertasDelMundo.jsx`.
const ESTRELLAS = [
  // el costado izquierdo
  [5, 14, 0.0], [12, 27, 1.3], [3, 41, 0.6], [16, 55, 2.1], [8, 68, 0.9],
  [14, 80, 1.7], [2, 62, 2.6], [19, 12, 0.4], [10, 90, 2.2], [6, 34, 1.1],
  // y el derecho
  [95, 16, 0.8], [86, 29, 1.9], [92, 44, 0.3], [81, 58, 2.4], [97, 70, 1.2],
  [88, 84, 0.5], [83, 10, 2.0], [90, 93, 1.5], [98, 36, 2.7], [84, 72, 0.7],
];

// Las fugaces: dos, en los dos costados, con la mitad del ciclo de diferencia para que no
// crucen a la vez. El retraso negativo las arranca ya empezadas, así que la primera no se hace
// esperar diecisiete segundos.
const FUGACES = [
  { left: "8%", top: "18%", delay: "-3s" },
  { left: "72%", top: "8%", delay: "-11.5s" },
];

/**
 * La primera pantalla. Lo que ve alguien que llega sin saber qué es esto.
 *
 * DOS COSAS QUE SE CAYERON DE AQUÍ, y las dos por el mismo motivo.
 *
 * La primera, **las cifras inventadas**. Decía «+3500 estudiantes» y «98% de satisfacción», y
 * ninguna de las dos era cierta: salían escritas a mano del backend (`students = 3500 + los
 * tests hechos`, `satisfaction = 98`) y nadie había preguntado nunca a nadie por su
 * satisfacción. En septiembre entran ciento ochenta alumnos, veinte veces menos. Un número
 * inflado en la primera pantalla de una plataforma que leen las familias de menores no es
 * marketing optimista: es una afirmación falsa firmada por quien responde. Ahora solo se
 * afirma **lo que se puede contar** —los cursos y las lecciones publicadas—, y se cuenta de
 * verdad en `GET /api/cifras`. Si la llamada falla, el bloque **no se pinta**: mejor no decir
 * nada que decir un número que no se ha comprobado.
 *
 * La segunda, **la foto del fundador**. Una cara enorme en la portada convierte la Academia en
 * una persona, y esto tiene que poder crecer sin depender de que alguien reconozca una cara.
 *
 * EL AIRE DE ARRIBA Y DE ABAJO NO ES DECORACIÓN. Esto era `justify-center` sin padding:
 * mientras el contenido cabía, bien; en cuanto creció —las competencias y la línea de
 * materias— se puso más alto que la pantalla, y **centrar algo más alto que su caja recorta
 * por arriba**. El titular quedaba pegado a la barra. Y la línea de «baja para ver las
 * materias» estaba en `absolute bottom-8`, fuera del flujo, así que las cifras se le metieron
 * encima. Ahora hay padding arriba y abajo, el bloque se centra con `my-auto` —que cede
 * cuando no hay sitio en vez de recortar— y la línea de bajar va en el flujo: no puede
 * solaparse por mucho que crezca lo de arriba.
 *
 * EL BOTÓN DICE LO QUE PASA AL PULSARLO. Ponía «Hacer el test», que no dice ni qué test ni
 * para qué; después dijo «Descubre tu forma de aprender».
 *
 * Y EL 7 DE OCTUBRE DE 2026 EL TEST SE VA DEL HERO. Dos motivos, y el segundo es el que
 * manda. El primero: había **dos botones al mismo test** —éste y el del final de la portada—
 * con dos nombres distintos, así que o uno de los dos mentía o eran dos tests, y no hay forma
 * de saberlo desde fuera. El segundo: lo que el test devuelve es **el orden de cursos que más
 * encaja**, o sea una respuesta a «¿por dónde empiezo?», y esa pregunta se la hace quien ya
 * está mirando la lista — no quien acaba de llegar. Así que el test se queda donde se hace la
 * pregunta: al lado del buscador de `/cursos` y en la sección de las cinco competencias.
 *
 * Lo que queda aquí es un solo botón y dice a dónde lleva: **Explora los cursos**.
 */

// «Materias» y no «cursos», que es lo que decidió el glosario: un curso es un nivel escolar
// y una materia es una asignatura. Y las dos dicen «publicadas» a propósito: contar también
// los borradores infla la cifra sin que se note, que es de donde venimos.
// LA ETIQUETA DECIA «MATERIAS» Y LO QUE SE CUENTA SON CURSOS. La clave ya se llamaba
// `cursos` y el backend hace un `count_documents` sobre `cms_courses`, o sea que las trece
// cosas contadas eran «Comunicacion 3.º ESO», «Educacion literaria 1.º ESO», «Latin I»: no
// son materias de ningun instituto, y materias hay siete. La cifra era correcta y la palabra
// no, que es la clase de error que nadie ve porque el numero cuadra. Glosario:
// `docs/estado/glosario.md`.
const CIFRAS = [
  { clave: "cursos", etiqueta: "Cursos publicados" },
  { clave: "lecciones", etiqueta: "Lecciones publicadas" },
];

export default function HeroLanding() {
  // `null` = todavía no se sabe. NO es cero, y la diferencia importa: un cero pintado con
  // aplomo mientras la respuesta viaja es tan falso como un número inventado.
  const [cifras, setCifras] = useState(null);

  // LA LECCIÓN DE MUESTRA DECIDE LA FORMA DE ESTA PANTALLA, y por eso se pregunta aquí y no
  // dentro de la tarjeta: con lección, el hero es de dos columnas y la lección es la mitad
  // derecha; sin ella —nadie la ha marcado en el CMS, o la han despublicado— vuelve a ser una
  // sola columna centrada, como era. Un hero que se queda con media pantalla vacía esperando
  // una respuesta es peor que uno que nunca la tuvo.
  const leccion = useMuestra();
  const dos = Boolean(leccion);

  // Lo que está a un clic de esta pantalla. Cuando el navegador esté ocioso, no antes:
  // durante la primera pintada la red la necesita lo que se está mirando.
  useEffect(() => {
    adelantaSinPrisa("cursos", TRAE_CURSOS);
    adelantaSinPrisa("curso", TRAE_CURSO, 2600);
  }, []);

  useEffect(() => {
    let vivo = true;
    getCifras()
      .then((c) => vivo && setCifras(c))
      .catch(() => vivo && setCifras(false));
    return () => { vivo = false; };
  }, []);

  return (
    <section
      className="relative flex flex-col overflow-hidden px-6 pt-14 pb-10 sm:pt-16 sm:pb-14"
      data-tono="nacar"
      data-testid="hero"
    >
      {/* EL CIELO SE VA DE AQUÍ, Y NO SE TIRA. Estaba pintado en blanco sobre el negro del
          hero: con el fondo claro no se vería ni una estrella. Gabri, 19 de octubre de 2026:
          *«el fondo estrellado podemos guardarlo a ver si sirve para las tarjetas negras»*.
          Las clases `cielo-hero`, `fugaz-hero` y `hero-luces` siguen en `index.css` y las
          estrellas siguen declaradas arriba, esperando a la tarjeta del Mundo. */}

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        /* EL ANCHO BAJA DE 1400 A 1320 y la pagina gana margen a los dos lados: con 1400 la
           tarjeta llegaba casi al borde de la ventana en una pantalla grande. */
        className={`relative z-10 w-full ${dos ? "max-w-[1320px]" : "max-w-4xl text-center"} mx-auto`}
      >
        <div className={`grid gap-8 lg:gap-14 items-stretch ${dos ? "lg:grid-cols-2" : "grid-cols-1"}`}>

          {/* LA COLUMNA QUE HABLA, CENTRADA EN SU MITAD. Gabri, 19 de octubre de 2026:
              *«centraría el título, las tres líneas, el botón y el encabezado... centrar
              respecto a la primera columna»*. Con todo alineado a la izquierda, el texto se
              pegaba al borde de la ventana y la tarjeta quedaba flotando lejos; centrado en su
              mitad, las dos columnas se miran. El `max-w` es lo que hace que centrar signifique
              algo: sin él, una línea que ocupa todo el ancho está centrada y no se nota. */}
          <div className="flex flex-col justify-center items-center text-center">
            <div className="inline-flex items-center gap-2.5 rounded-full bg-white px-4 py-2">
              <span className="w-1.5 h-1.5 rounded-full bg-alpha-red pulse-red" />
              <span className="font-mono-alpha text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-alpha-black/60">
                Humanidades · 1.º ESO a 2.º Bachillerato
              </span>
            </div>

            <h1 className="mt-7 font-serif-display text-[52px] sm:text-[76px] lg:text-[88px] leading-[0.9] tracking-tight text-balance text-alpha-black">
              Desarrolla tus<br />
              <span className="italic text-alpha-red">habilidades</span>
            </h1>

            {/* UN SOLO SUBTÍTULO, Y LO ESCRIBIÓ GABRI (19 oct 2026). Hubo un rato en que
                cambiaba según hubiera lección o no —con ella señalaba la tarjeta de al lado—,
                y sobraba: la tarjeta ya dice lo que es en su propio rótulo, y una frase que
                señala a su vecina depende de que la vecina esté. Éste vale con lección y sin
                ella, que es lo que tiene que hacer un subtítulo. */}
            <p className={`mt-6 text-base sm:text-lg text-alpha-black/60 leading-relaxed mx-auto ${dos ? "max-w-[460px]" : "max-w-2xl"}`}>
              Ofrecemos un refuerzo para mejorar las competencias que necesitas para afrontar tu futuro.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 items-center justify-center">
              {/* LOS BOTONES RESPONDEN AL DEDO Y AL FOCO, y eso es todo lo que hace falta. La
                  evaluación del 15 de septiembre pedía un «atractor magnético» —que el botón
                  persiga al cursor— y se descartó por dos motivos: **un botón que se mueve
                  cuando vas a pulsarlo es más difícil de pulsar**, y quien más lo nota es quien
                  peor puntería tiene; y esto se abre en el Chromebook del instituto, donde cada
                  píxel de animación continua compite con el mundo 3D. */}
              <motion.div whileTap={{ scale: 0.97 }} transition={{ type: "spring", stiffness: 420, damping: 26 }} className="w-full sm:w-auto">
                <Link
                  to="/cursos"
                  data-testid="hero-cta-cursos"
                  {...pistasDe("cursos", TRAE_CURSOS)}
                  /* DE ROJO A NEGRO el 19 de octubre de 2026, y es por el fondo: sobre claro,
                     un botón rojo relleno compite con el rojo del titular y con el de los
                     rótulos, y entonces el rojo deja de señalar nada. En negro, el rojo se
                     queda donde significa algo. */
                  className="w-full sm:w-auto px-7 py-4 bg-alpha-black text-white font-mono-alpha uppercase text-[12px] tracking-widest inline-flex items-center justify-center gap-3 rounded-full hover:bg-alpha-black/85 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-alpha-black focus-visible:ring-offset-2"
                >
                  Explora los cursos <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            </div>

            {/* LAS CIFRAS SUBEN A LA PRIMERA PANTALLA. Estaban debajo del pliegue: había que
                hacer scroll para ver que esto no es una demo, que es justo lo que hay que
                saber antes de decidir si merece la pena bajar.

                Y VAN EN COLUMNAS DE IGUAL ANCHO, no en una fila libre. Gabri, 19 de octubre de
                2026: *«son más largos que la palabra de debajo, y a mí me gusta la simetría»*.
                Con una fila libre cada bloque medía lo que medía su número, así que «2994»
                empujaba a su etiqueta y la fila se leía torcida; con dos columnas iguales y las
                dos líneas centradas, el eje no depende del número que toque ese día.

                SE PUEDEN ABRIR desde el 26 de septiembre: una cifra que no se puede abrir pide
                un acto de fe. Y el grupo va con NOMBRE (`group/cifra`) porque un `group-hover:`
                sin nombre responde a cualquier `group` de arriba. */}
            {cifras !== false && (
              <div className="mt-9 pt-6 border-t border-alpha-black/10 grid grid-cols-2 gap-3 mx-auto w-full max-w-[400px]">
                {CIFRAS.map(({ clave, etiqueta }, i) => (
                  <Link key={clave} to="/cursos" {...pistasDe("cursos", TRAE_CURSOS)}
                    className={`flex flex-col items-center text-center group/cifra ${i === 1 ? "border-l border-alpha-black/10" : ""}`}
                    data-testid={`hero-cifra-${clave}`}>
                    {/* `lining-nums` no es un capricho: Cormorant trae por defecto cifras
                        ELZEVIRIANAS —el 5 y el 8 bajan de la línea base, el 1 es corto— que son
                        preciosas dentro de un párrafo y se leen fatal como dato suelto. */}
                    <div className="font-serif-display text-[34px] sm:text-[40px] leading-none tabular-nums
                                    lining-nums group-hover/cifra:text-alpha-red transition-colors">
                      {cifras ? cifras[clave] : "—"}
                    </div>
                    <div className="font-mono-alpha text-[9px] uppercase tracking-[0.16em]
                                    text-alpha-black/45 mt-2.5 group-hover/cifra:text-alpha-black/75 transition-colors">
                      {etiqueta}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* LA COLUMNA QUE ENSEÑA. La tarjeta se pinta sola si hay muestra; si no la hay,
              `dos` es falso y esta columna no llega a existir. */}
          {dos && <Pruebala leccion={leccion} enElHero />}
        </div>
      </motion.div>
    </section>
  );
}
